import { defaultMergeReducer, Keys, localStorageSync as ngLocalStorageSync, rehydrateApplicationState } from 'ngrx-store-localstorage';
import { omit, pick } from 'lodash-es';
import { Action, ActionReducer } from '@ngrx/store';

import { LocalStorageConfig } from './storage.types';
import { createStorageKeyDeserializer, createStorageKeySerializer, isStorageChangeAction } from './storage.helpers';

const INIT_ACTION = '@ngrx/store/init';
const UPDATE_ACTION = '@ngrx/store/update-reducers';

const createMergeReducer = <T extends object>(rehydratedKeys?: readonly (keyof T)[]) => (state: T, rehydratedState: T | undefined, action: Action): unknown => {
  if ((action.type === INIT_ACTION || action.type === UPDATE_ACTION) && rehydratedState) {
    return defaultMergeReducer(state, pick(rehydratedState, rehydratedKeys || []), action);
  }

  return state;
};

// Inspired by: https://github.com/btroncone/ngrx-store-localstorage/issues/40
export const localStorageSync = <T extends object = object, V extends Action = Action>({ serializationKey, keys: rehydratedKeys, initialState, ...config }: LocalStorageConfig<T>): (reducer: ActionReducer<T, V>) => ActionReducer<T, V> => {
  const storageKeySerializer = createStorageKeySerializer(serializationKey);
  const storageKeyDeserializer = createStorageKeyDeserializer(serializationKey);
  const keys: Keys = initialState ? Object.keys(initialState) : [ ...(rehydratedKeys || []) ].map(key => String(key));
  const localStorageSyncReducer = ngLocalStorageSync({
    rehydrate: true,
    storageKeySerializer,
    keys,
    mergeReducer: createMergeReducer(rehydratedKeys),
    ...config,
  });

  return (reducer: ActionReducer<T, V>): ActionReducer<T, V> => (state, action) => {
    const key = isStorageChangeAction(action) && storageKeyDeserializer(action.key);

    if (key) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const rehydratedState = rehydrateApplicationState([ key ] as Keys, localStorage, storageKeySerializer, true) || {};

      return { ...omit(state || {}, [ key ]), ...rehydratedState } as T;
    }

    return localStorageSyncReducer(reducer)(state, action) as T;
  };
};
